import React, { useEffect, useMemo } from 'react';
import { OlLayerType, useMapLayer } from '../hooks/useMapLayer';
import { geometry } from './geometries/counties';
import { useTheme } from '../helpers/hooks';
import type { Theme } from '@/helpers/hooks';
import { colors } from '../utils/colors';
import { getCenter } from 'ol/extent';
import { Geometry } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { useMapContext } from './context/MapContext';
import { useSelector } from 'react-redux';
import { selectLayers } from '../plotter/plotterSelectors';
// import GeoJSON from 'ol/format/GeoJSON';

export type MapCountiesProps = {
  zIndex: number;
};

export const getLabelStyle = (theme: Theme) => {
  return {
    'text-value': ['get', 'text'],
    'text-fill-color': colors.map[theme]?.countyLabelFill,
    'text-stroke-width': 2,
    'text-stroke-color': colors.map[theme]?.countyLabelStroke,
    'text-align': 'center',
    'text-rotate-with-view': true,
    'text-font': '14px Calibri,sans-serif',
  };
};

const getStyle = (theme: Theme) => {
  return {
    'stroke-width': 2,
    'stroke-color': colors.map[theme]?.countyStroke,
    'stroke-line-dash': [10, 10],
    // 'fill-color': colors.map[theme]?.countyFill,
  };
};

export const MapCounties = ({ zIndex }: MapCountiesProps) => {
  const [theme] = useTheme();
  const { map } = useMapContext();
  const layers = useSelector(selectLayers);

  const [showCountyLabels, setShowCountyLabels] = React.useState(false);

  useEffect(() => {
    if (!map) return;
    const callback = () => {
      const zoom = map.getView().getZoom() ?? -1;
      const show = zoom >= 8;
      if (showCountyLabels !== show) {
        setShowCountyLabels(show);
      }
    };
    map.getView().on('change:resolution', callback);
    return () => {
      map.getView().un('change:resolution', callback);
    };
  }, [map, showCountyLabels]);

  const layerStyle = useMemo(() => {
    return getStyle(theme);
  }, [theme]);

  const counties = useMemo(() => {
    return layers.counties ? geometry.features : [];
  }, [layers.counties]);

  // @ts-expect-error
  useMapLayer(counties, {
    zIndex,
    style: layerStyle,
    properties: { interactable: false },
  });

  const countyLabels = useMemo(() => {
    if (!showCountyLabels || !layers.counties) return [];
    return geometry.features.map((featureJson) => {
      const feature = new GeoJSON().readFeature(
        featureJson,
      ) as Feature<Geometry>;
      const { properties } = featureJson;
      const geometry = feature.getGeometry();
      return {
        type: 'Feature' as 'Feature',
        geometry: {
          type: 'Point',
          coordinates: geometry ? getCenter(geometry.getExtent()) : [0, 0],
        },
        properties: {
          id: properties?.GEOID,
          key: `county-${properties?.GEOID}`,
          text: properties?.NAME ?? '',
          type: 'county',
        },
      };
    });
  }, [showCountyLabels, layers.counties]);

  const style = useMemo(() => getLabelStyle(theme), [theme]);

  useMapLayer(countyLabels, {
    zIndex: zIndex + 0.5,
    style,
    layerType: OlLayerType.Vector,
    properties: { interactable: false },
  });

  return <></>;
};
